/**
 * 可灵2.5 Turbo Pro 视频生成节点
 * 支持文生视频和图生视频
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './KlingVideoNode.css';

const apiService = new ApiService();

// 宽高比选项
const aspectRatioOptions = [
  { value: '16:9', label: '16:9 横屏' },
  { value: '9:16', label: '9:16 竖屏' },
  { value: '1:1', label: '1:1 方形' },
];

// 时长选项
const durationOptions = [5, 10];

export const KlingVideoNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [duration, setDuration] = useState(data.duration || 5);
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImage, setConnectedImage] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
      duration,
      negativePrompt,
    });
  }, [id, aspectRatio, duration, negativePrompt, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  // 获取连接到此节点的图片节点（只取第一张）
  const getConnectedImageNode = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    console.log(`KlingVideoNode ${id} - 连接到图片输入的边:`, connectedEdges.length);
    
    if (connectedEdges.length === 0) {
      return null;
    }

    // 只取第一个连接的图片
    const edge = connectedEdges[0];
    const sourceNode = getNode(edge.source);
    console.log(`KlingVideoNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);
    
    // 从图片参考节点获取图片
    if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
      const url = sourceNode.data.imageUrl;
      if (!url.startsWith('data:') && !url.startsWith('blob:')) {
        console.log(`KlingVideoNode ${id} - 从图片参考节点获取图片:`, url);
        return url;
      }
    }
    
    // 从图片展示节点获取图片
    if (sourceNode && sourceNode.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
      const url = sourceNode.data.imageUrl;
      if (!url.startsWith('data:') && !url.startsWith('blob:')) {
        console.log(`KlingVideoNode ${id} - 从图片展示节点获取图片:`, url);
        return url;
      }
    }
    
    // 从手绘节点获取图片（只使用已上传到云端的图片）
    if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
      const url = sourceNode.data.imageUrl;
      if (!url.startsWith('data:') && !url.startsWith('blob:')) {
        console.log(`KlingVideoNode ${id} - 从手绘节点获取图片:`, url);
        return url;
      } else if (sourceNode.data?.isLocalPreview) {
        console.warn(`KlingVideoNode ${id} - 手绘节点图片为本地预览，跳过`);
      }
    }
    
    // 从图片任务节点获取生成的图片（取第一张）
    if (sourceNode && sourceNode.type === 'imageTask') {
      console.log(`KlingVideoNode ${id} - 任务节点数据:`, {
        status: sourceNode.data?.status,
        generatedImages: sourceNode.data?.generatedImages,
        imagesCount: sourceNode.data?.generatedImages?.length
      });
      
      if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
        const firstImage = sourceNode.data.generatedImages[0];
        const url = typeof firstImage === 'string' ? firstImage : firstImage.url;
        if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
          console.log(`KlingVideoNode ${id} - 从任务节点获取第一张图片:`, url);
          return url;
        }
      }
    }
    
    // 从视频任务节点获取生成的视频封面
    if (sourceNode && sourceNode.type === 'videoTask') {
      if (sourceNode.data?.coverImageUrl) {
        const url = sourceNode.data.coverImageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          console.log(`KlingVideoNode ${id} - 从视频任务节点获取封面:`, url);
          return url;
        }
      }
    }
    
    return null;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片
  useEffect(() => {
    const imageUrl = getConnectedImageNode();
    setConnectedImage(imageUrl);
  }, [getConnectedImageNode]);

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n');
      console.log('使用提示词:', prompt);
      
      // 2. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 3. 根据是否有图片选择不同的 app_name
      const appName = connectedImage 
        ? 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video'
        : 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video';

      // 4. 构建请求参数
      const inputParams = {
        prompt: prompt,
        duration: duration,
      };

      // 如果有连接的图片，则为图生视频模式
      if (connectedImage) {
        inputParams.start_image = connectedImage;
        console.log('图生视频模式，使用参考图片:', connectedImage);
      } else {
        // 文生视频模式，需要指定宽高比
        inputParams.aspect_ratio = aspectRatio;
        console.log('文生视频模式，宽高比:', aspectRatio);
      }

      // 如果有反向提示词，添加到参数中
      if (negativePrompt.trim()) {
        inputParams.negative_prompt = negativePrompt.trim();
      }
      
      console.log('请求参数:', {
        app_name: appName,
        input_params: inputParams
      });
      
      // 5. 调用可灵 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          input_params: inputParams
        }
      );
      
      console.log('可灵任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        
        // 根据模式提示不同的信息
        const taskTypeLabel = connectedImage ? '图生视频' : '文生视频';
        
        toast.success(`${taskTypeLabel}任务已创建！`);
        
        // 6. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        // 7. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: `可灵2.5 ${taskTypeLabel}`,
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 8. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建视频任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('可灵视频生成失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    // 文本接入点：放宽为只要来源节点存在即可，后续生成时再校验
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      if (!sourceNode) {
        return true;
      }

      const validTextTypes = ['promptInput', 'note'];
      if (!validTextTypes.includes(sourceNode.type)) {
        toast.warning('请连接文本输入节点');
        return false;
      }
      return true;
    }
    
    // 图片接入点：允许常见图片来源节点，数量限制依然保留
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      if (!sourceNode) {
        return true;
      }

      const validImageTypes = ['imageReference', 'imageDisplay', 'drawing', 'imageTask', 'videoTask'];
      if (!validImageTypes.includes(sourceNode.type)) {
        toast.warning('请连接图片节点');
        return false;
      }
      
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      if (imageEdgesCount >= 1) {
        toast.warning('可灵2.5最多支持1张参考图片');
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [getNode, getEdges, id]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'klingVideo', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="KlingVideoNode-container">
      {/* 复制按钮 */}
      <button 
        className="KlingVideoNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="KlingVideoNode-handle KlingVideoNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '25%' }}
      >
        <div 
          className="KlingVideoNode-handle-label KlingVideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="KlingVideoNode-handle KlingVideoNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '75%' }}
      >
        <div 
          className="KlingVideoNode-handle-label KlingVideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多1张）"
        >
          图片
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="KlingVideoNode-handle KlingVideoNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="KlingVideoNode-title">可灵2.5 视频</div>

      {/* 内容区域 */}
      <div className="KlingVideoNode-wrapper">
        {/* 宽高比选择 - 只在文生视频模式显示 */}
        {!connectedImage && (
          <div className="KlingVideoNode-field">
            <label className="KlingVideoNode-label">宽高比</label>
            <div className="KlingVideoNode-button-group">
              {aspectRatioOptions.map((option) => (
                <button
                  key={option.value}
                  className={`KlingVideoNode-button ${aspectRatio === option.value ? 'KlingVideoNode-button-active' : ''}`}
                  onClick={() => setAspectRatio(option.value)}
                  disabled={isGenerating || readOnly}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 时长选择 */}
        <div className="KlingVideoNode-field">
          <label className="KlingVideoNode-label">时长</label>
          <div className="KlingVideoNode-button-group KlingVideoNode-button-group-2col">
            {durationOptions.map((dur) => (
              <button
                key={dur}
                className={`KlingVideoNode-button ${duration === dur ? 'KlingVideoNode-button-active' : ''}`}
                onClick={() => setDuration(dur)}
                disabled={isGenerating || readOnly}
              >
                {dur}秒
              </button>
            ))}
          </div>
        </div>

        {/* 反向提示词输入 */}
        <div className="KlingVideoNode-field">
          <label className="KlingVideoNode-label">反向提示词（可选）</label>
          <textarea
            className="KlingVideoNode-textarea"
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            placeholder="描述不希望在视频中出现的内容，如：people, animals, text, watermark"
            disabled={isGenerating || readOnly}
            rows={3}
          />
        </div>

        {/* 参考图片展示 */}
        <div className="KlingVideoNode-field">
          <label className="KlingVideoNode-label">
            参考图片 {connectedImage ? '(图生视频)' : '(文生视频)'}
          </label>
          {connectedImage ? (
            <div className="KlingVideoNode-image-preview nodrag">
              <img 
                src={connectedImage} 
                alt="参考图"
                className="KlingVideoNode-preview-img"
              />
              <div className="KlingVideoNode-image-badge">
                首帧图片
              </div>
            </div>
          ) : (
            <div className="KlingVideoNode-image-placeholder">
              🖼️ 连接图片节点可启用图生视频模式
            </div>
          )}
        </div>

        {/* 连接提示 */}
        <div className="KlingVideoNode-hint">
          💡 <strong>使用说明</strong><br />
          • <strong>文生视频</strong>：纯文字描述生成视频<br />
          • <strong>图生视频</strong>：基于参考图片生成动画<br />
          • 支持专业级动作和电影般的深度效果<br />
          点击生成后会自动创建视频任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`KlingVideoNode-generate ${isGenerating || readOnly ? 'KlingVideoNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="KlingVideoNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

KlingVideoNode.displayName = 'KlingVideoNode';

